import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { XCircle } from 'lucide-react';
import { Button, ConfirmDialog } from '@mlm/ui';
import { cancelMyOrder, type OrderDetail, type OrderListRow } from '../../lib/api';
import { resetLockInputs } from '../../lib/lock-inputs';

/**
 * "Cancel order" for an unpaid order, plus the one-liner shown in its place
 * while a payment is being confirmed.
 *
 * Lives beside `order-payment-panels.tsx` for the same reason that file exists:
 * three surfaces render this (the order detail, the orders list, and the locked
 * payment page), and a copy per surface is how they drift.
 *
 * Visibility is NOT decided here. The server stamps `cancellable` on the order —
 * two of its inputs are rows the client can't see — so this component only
 * renders what it is told.
 */

type CancelableOrder = Pick<OrderDetail | OrderListRow, 'id' | 'code'> & {
  checkoutGroupId?: string | null;
  cancellable?: boolean;
  cancelBlock?: string | null;
};

/**
 * The two blocks worth explaining. Every other reason (wrong status, already
 * cancelled) is self-evident from the order itself, so saying nothing is right.
 */
const EXPLAINED_BLOCKS = new Set(['payment_under_review', 'payment_in_flight']);

export function CancelOrderCta({
  order,
  groupSize,
  variant = 'panel',
  onCancelled,
}: {
  order: CancelableOrder;
  /** How many orders one payment covers, when this order is part of a group. */
  groupSize?: number;
  /** `panel` on the detail/pay screens, `inline` for the orders-list footer. */
  variant?: 'panel' | 'inline';
  onCancelled?: () => void;
}) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [errMsg, setErrMsg] = useState<string | null>(null);

  const mut = useMutation({
    meta: { successMessage: 'Order cancelled.' },
    mutationFn: () => cancelMyOrder(order.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['order', order.id] });
      qc.invalidateQueries({ queryKey: ['orders'] });
      qc.invalidateQueries({ queryKey: ['orders', 'action-summary'] });
      if (order.checkoutGroupId) {
        qc.invalidateQueries({ queryKey: ['order-group', order.checkoutGroupId] });
      }
      // Load-bearing under `unpaid_order_lock_enabled`: the guard reads a
      // separate cached query, and without this reset the member is bounced
      // straight back to the payment page by the order they just cancelled.
      resetLockInputs(qc);
      setOpen(false);
      setErrMsg(null);
      onCancelled?.();
    },
    onError: (err: unknown) => {
      const data = (err as { response?: { data?: { error?: string; message?: string } } }).response
        ?.data;
      // The two money guards are worth saying plainly — the member is about to
      // wonder why a button they just clicked refused.
      if (data?.error === 'payment_under_review') {
        setErrMsg("We're still checking a payment you submitted for this order.");
      } else if (data?.error === 'payment_in_flight') {
        setErrMsg('An online payment for this order is still going through.');
      } else if (data?.error === 'group_partially_settled') {
        setErrMsg('Part of this checkout has already been paid. Please contact support.');
      } else {
        setErrMsg(data?.message ?? data?.error ?? 'Failed to cancel this order');
      }
    },
  });

  if (!order.cancellable) {
    if (variant === 'inline' || !EXPLAINED_BLOCKS.has(order.cancelBlock ?? '')) return null;
    return (
      <p className="text-xs text-muted-foreground">
        We&apos;re confirming a payment on this order — it can&apos;t be cancelled right now.
      </p>
    );
  }

  const covers = groupSize && groupSize > 1 ? groupSize : null;

  return (
    <>
      <Button
        size="sm"
        variant={variant === 'inline' ? 'ghost' : 'outline'}
        className={variant === 'inline' ? 'h-8' : 'gap-1.5'}
        onClick={() => setOpen(true)}
      >
        {variant === 'panel' && <XCircle className="h-4 w-4" aria-hidden />}
        Cancel order
      </Button>
      <ConfirmDialog
        open={open}
        onOpenChange={(o) => {
          if (!o && !mut.isPending) {
            setOpen(false);
            setErrMsg(null);
          }
        }}
        title={covers ? 'Cancel this checkout?' : 'Cancel this order?'}
        description={
          covers ? (
            <>
              One payment covers <strong>{covers} orders</strong>, so all of them will be cancelled.
              This can&apos;t be undone — you&apos;d need to order again.
            </>
          ) : (
            <>
              Order <span className="font-mono">{order.code}</span> will be cancelled. This
              can&apos;t be undone — you&apos;d need to order again.
            </>
          )
        }
        confirmLabel={covers ? 'Yes, cancel them' : 'Yes, cancel it'}
        confirmVariant="destructive"
        errorMessage={errMsg}
        loading={mut.isPending}
        onConfirm={async () => {
          await mut.mutateAsync();
        }}
      />
    </>
  );
}
